import { useState, useEffect, useRef } from "react";
import styles from "styles/header.module.css";
import {
  IconButton,
  OutlinedInput,
  InputLabel,
  FormControl,
  Select,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  Typography,
  Box,
  Badge,
  Popper,
  Card,
  ClickAwayListener,
  Grow,
  Stack,
  Tooltip,
  useMediaQuery,
  List,
  ListItemText,
  ListItem,
  Skeleton,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
} from "@mui/material";
import SupervisorAccountRoundedIcon from "@mui/icons-material/SupervisorAccountRounded";
import AccountCircleRoundedIcon from "@mui/icons-material/AccountCircleRounded";
import Logout from "@mui/icons-material/Logout";
import DoneIcon from "@mui/icons-material/Done";


import { useTheme } from "@mui/material/styles";
import UserService from "services/UserService.js";
import PatientService from "services/PatientService.js";
import StringUtils from "utils/StringUtils.js";
import Authenticator from "services/Authenticator.js";
import { getOrganizationFromHostname } from "utils/OrganizationUtils.js";
import { useNavigate } from "react-router-dom";
import useAPI from "hooks/useAPI.js";
import { useSelector, useDispatch } from "react-redux";
import { selectHospital, focusHospital } from "state/slices/hospitalSlice";
import { selectAuthUser, updateAuthUser } from "state/slices/authUserSlice";
import Cookies from "universal-cookie";
import moment from "moment";
import { safeIdentify, safeSetUserProperties } from "utils/MixpanelUtils.js";
import LanguageDropdown from "components/headers/LanguageDropdown";
import GlobalPatientSearch from "components/headers/GlobalPatientSearch";

const cookies = new Cookies();

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 175,
    },
  },
};

function HospitalHeader() {
  const organization = getOrganizationFromHostname();
  const organizationDisplayText =
    organization ? `Melillo Center – ${organization}` : "";

  return (
    <div
      id={styles["header"]}
      className="d-flex flex-row no-wrap align-items-center justify-content-between"
    >
      <div
        className="d-flex flex-row no-wrap align-items-center"
        style={{ minWidth: "220px" }}
      >
        {organization && (
          <>
            <Chip
              label={organization}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ ml: 2 }}
            />
            <Typography
              sx={{
                ml: 1,
                fontSize: "0.875rem",
                color: "text.light",
                fontWeight: 500,
              }}
            >
              {organizationDisplayText}
            </Typography>
          </>
        )}
      </div>
      <div className="d-flex flex-row no-wrap align-items-center" style={{ gap: 8 }}>
        <GlobalPatientSearch />
        <HospitalsSelectInput />
        <LanguageDropdown compact />
        <AddNewDropdown />
        <NotificationButton />
        <UserDropdown />
      </div>
    </div>
  );
}

function HospitalsSelectInput() {
  const theme = useTheme();
  const [hospitals, setHospitals] = useState([]);

  useAPI(UserService.getHospitals, getHospitalsHandler, false, true);

  const focusedHospital = useSelector(selectHospital);
  const dispatch = useDispatch();

  /* API success handlers */
  function getHospitalsHandler(response) {
    let hospitalList = response.data;

    setHospitals(hospitalList);

    let focusHospitalId = hospitalList[0].id;

    // set focused hospital if already selected earlier (only if it still exists in the list)
    const cookieHospitalId = cookies.get("hospital");
    if (cookieHospitalId && hospitalList.some((h) => String(h.id) === String(cookieHospitalId))) {
      focusHospitalId = cookieHospitalId;
    }
    dispatch(focusHospital({ id: focusHospitalId }));
  }

  function getStyles(hospitalId) {
    return {
      fontWeight: focusedHospital.id !== hospitalId ? 400 : 600,
    };
  }

  const handleChange = (event) => {
    const {
      target: { value },
    } = event;

    // Set hospital id in cookies to persist selected hospital (otherwise the selected hospital changes on page reload)
    cookies.set("hospital", value, { path: "/" });

    dispatch(focusHospital({ id: value }));
  };

  const isSelectDisabled =
    /^\/hospital\/emr-management\/patients\/\d+\/.+/.test(
      window.location.pathname
    );

  return (
    <FormControl
      sx={{
        mr: "6px",
        width: 220,
        height: 40,
      }}
      size="small"
    >
      <InputLabel id="hospital-branches-select-label">Centre</InputLabel>
      <Select
        labelId="hospital-branches-select-label"
        sx={{
          background: theme.palette.background.default,
          borderRadius: "10px",
        }}
        IconComponent={() => (
          <img
            id={styles["map-marker-icon"]}
            src="assets/map-marker-icon.svg"
            alt="Map marker icon"
          />
        )}
        id="hospital-branches-select"
        value={hospitals.length === 0 ? "" : focusedHospital.id}
        onChange={handleChange}
        input={<OutlinedInput label="Branch" />}
        MenuProps={MenuProps}
        disabled={isSelectDisabled}
      >
        {hospitals.map((hospital) => (
          <MenuItem
            key={hospital.id}
            value={hospital.id}
            style={getStyles(hospital.id)}
          >
            {hospital.hospitalName}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function AddNewDropdown() {
  let navigate = useNavigate();
  const [anchorAddNewDropdownEl, setAnchorAddNewDropdownEl] = useState(null);
  const addNewDropdownOpen = Boolean(anchorAddNewDropdownEl);
  const handleAddNewDropdownClick = (event) => {
    setAnchorAddNewDropdownEl(event.currentTarget);
  };
  const handleAddNewDropdownClose = () => {
    setAnchorAddNewDropdownEl(null);
  };

  return (
    <div>
      <IconButton
        aria-label="more"
        id="add-new-btn"
        aria-controls={addNewDropdownOpen ? "add-new-menu" : undefined}
        aria-expanded={addNewDropdownOpen ? "true" : undefined}
        aria-haspopup="true"
        size="large"
        sx={{
          background: "#2563eb",
          width: 40,
          height: 40,
          borderRadius: "10px",
          border: "1px solid #dbeafe",
          boxShadow: "0 6px 14px rgba(37, 99, 235, 0.22)",
          "&:hover": {
            background: "#1d4ed8",
          },
        }}
        disableRipple
        onClick={handleAddNewDropdownClick}
      >
        <img src="assets/plus-icon.svg" alt="Add icon" />
      </IconButton>
      <Menu
        id="add-new-menu"
        MenuListProps={{
          "aria-labelledby": "add-new-btn",
        }}
        anchorEl={anchorAddNewDropdownEl}
        open={addNewDropdownOpen}
        onClose={handleAddNewDropdownClose}
        onClick={handleAddNewDropdownClose}
        PaperProps={{
          style: {
            maxHeight: ITEM_HEIGHT * 4.5,
            width: "20ch",
          },
        }}
      >
        <MenuItem
          onClick={() =>
            navigate("/hospital/emr-management/patients/add-patient")
          }
        >
          Add Patient
        </MenuItem>
      </Menu>
    </div>
  );
}

function UserDropdown() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const getUserAPI = useAPI(UserService.getUser, getUserHandler, false, true);

  const authUser = useSelector(selectAuthUser);
  const dispatch = useDispatch();

  const [isAdmin, setIsAdmin] = useState(false);

  /* API success handlers */

  function getUserHandler(response) {
    dispatch(updateAuthUser(response.data));
    let role = response.data.roles[0];
    if (role.typeEnum === "ORGANIZATION_ADMIN") {
      setIsAdmin(true);
    }
    if (response.data?.email) {
      safeIdentify(response.data.email);
      safeSetUserProperties({
        $email: response.data.email,
        $first_name: response.data.firstName,
        $last_name: response.data.lastName,
      });
    }
  }

  return (
    <div
      id={styles["user-profile-box"]}
      className="d-flex flex-row no-wrap align-items-center justify-content-between"
    >
      <Avatar
        sx={{
          width: 32,
          height: 32,
        }}
        src={authUser.profilePhoto}
        {...StringUtils.stringAvatar(
          `${authUser.firstName} ${authUser.lastName}`
        )}
      />
      <div
        className="d-flex flex-column justify-content-between ms-2"
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            lineHeight: "18px",
          }}
          color={theme.palette.text.dark}
        >
          {`${authUser.firstName} ${authUser.lastName}`}
        </Typography>
        <Typography
          variant="caption"
          mt={0}
          sx={{
            lineHeight: "14px",
          }}
          color={theme.palette.text.light}
        >
          {authUser.email}
        </Typography>
      </div>
      <div>
        <IconButton
          aria-label="more"
          id="profile-btn"
          aria-controls={open ? "profile-menu" : undefined}
          aria-expanded={open ? "true" : undefined}
          aria-haspopup="true"
          onClick={handleClick}
        >
          <img src="assets/kebab-grey-icon.svg" alt="Profile menu icon" />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          id="profile-menu"
          MenuListProps={{
            "aria-labelledby": "profile-btn",
          }}
          open={open}
          onClose={handleClose}
          onClick={handleClose}
          PaperProps={{
            elevation: 0,
            sx: {
              overflow: "visible",
              filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
              mt: 1.5,
              "& .MuiAvatar-root": {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              "&:before": {
                content: '""',
                display: "block",
                position: "absolute",
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: "background.paper",
                transform: "translateY(-50%) rotate(45deg)",
                zIndex: 0,
              },
            },
          }}
          transformOrigin={{
            horizontal: "right",
            vertical: "top",
          }}
          anchorOrigin={{
            horizontal: "right",
            vertical: "bottom",
          }}
        >
          <MenuItem onClick={() => navigate("/user/profile")}>
            <ListItemIcon>
              <AccountCircleRoundedIcon fontSize="medium" />
            </ListItemIcon>
            Profile
          </MenuItem>
          <Divider />{" "}
          {isAdmin ? (
            <div>
              <MenuItem onClick={() => navigate("/admin/organization")}>
                <ListItemIcon>
                  <SupervisorAccountRoundedIcon fontSize="medium" />
                </ListItemIcon>
                Admin
              </MenuItem>
              <Divider />
            </div>
          ) : null}
          <MenuItem onClick={() => Authenticator.logout()}>
            <ListItemIcon>
              <Logout fontSize="small" />
            </ListItemIcon>
            Logout
          </MenuItem>
        </Menu>
      </div>
    </div>
  );
}

function NotificationButton() {
  const theme = useTheme();
  const matchesXs = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useDispatch();

  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await UserService.getUserNotifications();
      // Reverse the array to show most recent first
      const reversedNotifications = response.data.reverse();
      setNotifications(reversedNotifications);
    } catch (error) {
      console.error("Failed to fetch notifications", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    const intervalId = setInterval(fetchNotifications, 30000);
    return () => clearInterval(intervalId);
  }, []);

  const unreadCount = notifications.filter((n) => !n.isRead).length;
  const filteredNotifications = notifications.filter((n) => {
    if (viewMode === "unread" && n.isRead) return false;
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      String(n.title || "").toLowerCase().includes(query) ||
      String(n.message || "").toLowerCase().includes(query)
    );
  });

  const handleToggle = () => setOpen((prev) => !prev);

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleMarkAllRead = async () => {
    try {
      await UserService.markAllNotificationsAsRead();
      await fetchNotifications();
    } catch (error) {
      console.error("Failed to mark all notifications as read", error);
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await UserService.markNotificationAsRead(id);
      await fetchNotifications();
    } catch (error) {
      console.error("Failed to mark notification as read", error);
    }
  };
  const navigate = useNavigate();

  function handleNotificationNavigation(notification) {
    if (!notification.patientId) {
      return;
    }
    if (notification.notificationType === "INTAKE_BUNDLE_COMPLETE") {
      navigate(`/hospital/emr-management/patients/${notification.patientId}/profile`, {
        state: { defaultTab: "Forms", intakeId: notification.referenceId }
      });
    } else if (notification.notificationType === "PATIENT_ALERT") {
      navigate(`/hospital/emr-management/patients/${notification.patientId}/profile`, {
        state: { defaultTab: "Alerts" }
      });
    }
  }

  const handleNotificationClick = async (notification) => {
    try {
      await UserService.markNotificationAsRead(notification.id);
    } catch (err) {
      console.error("Failed to mark read", err);
    }
    setOpen(false);
    if (!notification.patientId) {
      return;
    }

    // Dynamically set the center by finding the patient in the "All Centres" list
    try {
      // hospitalId = 0 allows fetching patients across all hospitals the user has access to
      const allPatientsResponse = await PatientService.getAllPatients([0]);
      const patients = allPatientsResponse.data.content || [];
      const targetPatient = patients.find(p => String(p.id) === String(notification.patientId));

      if (targetPatient && targetPatient.associatedHospitalId) {
        const hId = targetPatient.associatedHospitalId;
        cookies.set("hospital", hId, { path: "/" });
        dispatch(focusHospital({ id: hId }));
      }
    } catch (err) {
      console.error("Failed to dynamically set hospital center", err);
    }

    handleNotificationNavigation(notification);
  };


  return (
    <Box sx={{ flexShrink: 0, mx: "8px" }}>
      <IconButton ref={anchorRef} onClick={handleToggle} size="large">
        <Badge
          badgeContent={unreadCount}
          color="success"
          sx={{ "& .MuiBadge-badge": { top: 2, right: 4 } }}
        >
          <img
            src="assets/notification-icon.svg"
            alt="Notifications"
            style={{ width: 24, height: 24 }}
          />
        </Badge>
      </IconButton>
      <Popper
        placement={matchesXs ? "bottom" : "bottom-end"}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        style={{ zIndex: 1300 }}
        popperOptions={{
          modifiers: [{ name: "offset", options: { offset: [0, 9] } }],
        }}
      >
        {({ TransitionProps }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: matchesXs ? "center top" : "right top",
            }}
          >
            <Card
              sx={{
                borderRadius: 1.5,
                width: "100%",
                minWidth: 285,
                maxWidth: 420,
                [theme.breakpoints.down("md")]: { maxWidth: 285 },
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <Box>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ p: 2 }}
                  >
                    <Typography sx={{ fontSize: "16px", fontWeight: 600 }}>
                      Notifications
                    </Typography>
                    <Typography
                      color="primary"
                      sx={{ cursor: unreadCount > 0 ? "pointer" : "not-allowed", opacity: unreadCount > 0 ? 1 : 0.5 }}
                      onClick={handleMarkAllRead}
                    >
                      Mark all read
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} sx={{ px: 2, pb: 1, alignItems: "center" }}>
                    <ToggleButtonGroup
                      size="small"
                      value={viewMode}
                      exclusive
                      onChange={(_, val) => val && setViewMode(val)}
                    >
                      <ToggleButton value="all">All</ToggleButton>
                      <ToggleButton value="unread">Unread</ToggleButton>
                    </ToggleButtonGroup>
                    <TextField
                      size="small"
                      placeholder="Search"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      sx={{ flexGrow: 1 }}
                    />
                  </Stack>
                  <List
                    component="nav"
                    sx={{
                      maxHeight: 320,
                      overflowY: "auto",
                      width: { xs: 285, md: 420 },
                      "&::-webkit-scrollbar": { width: "6px" },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: theme.palette.divider,
                        borderRadius: "4px",
                      },
                    }}
                  >
                    {open && loading ? (
                      [1, 2, 3].map((i) => (
                        <ListItem
                          key={i}
                          divider
                          alignItems="center"
                          sx={{ py: 1.5, width: { md: 420, xs: 285 } }}
                        >
                          <ListItemText
                            primary={<Skeleton width="80%" height={20} />}
                            secondary={
                              <Stack spacing={2}>
                                <Skeleton width="90%" height={20} />
                                <Skeleton width="40%" height={16} />
                              </Stack>
                            }
                          />
                        </ListItem>
                      ))
                    ) : !loading && filteredNotifications.length === 0 ? (
                      <ListItem
                        divider
                        alignItems="center"
                        sx={{ py: 2, justifyContent: "center" }}
                      >
                        <Typography color="text.secondary" fontStyle="italic">
                          No notifications
                        </Typography>
                      </ListItem>
                    ) : (
                      filteredNotifications.map((n) => (
                        <ListItem
                          key={n.id}
                          divider
                          alignItems="center"
                          sx={{
                            bgcolor: n.isRead ? "transparent" : "#EFF8FF",
                            py: 1.5, cursor: "pointer",
                          }}
                          onClick={() => handleNotificationClick(n)}
                          secondaryAction={
                            !n.isRead && (
                              <Tooltip title="Mark as read">
                                <IconButton
                                  edge="end"
                                  size="small"
                                  sx={{ color: "primary.main" }}
                                  onClick={() => handleMarkAsRead(n.id)}
                                >
                                  <DoneIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )
                          }
                        >
                          <ListItemText
                            primary={
                              <Typography fontWeight="bold" fontSize="15px">
                                {n.title}
                              </Typography>
                            }
                            secondary={
                              <Stack spacing={1}>
                                <Typography>{n.message}</Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {moment.utc(n.createdAt, "YYYY-MM-DD hh:mm a").local().fromNow()}
                                </Typography>
                              </Stack>
                            }
                          />
                        </ListItem>
                      ))
                    )}
                  </List>
                </Box>
              </ClickAwayListener>
            </Card>
          </Grow>
        )}
      </Popper>
    </Box>
  );
}

export default HospitalHeader;
